import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';
import { BazisControlFieldStartComponent } from '@bazis/form/components/parts/control-field-start.component';
import { BazisControlFieldEndComponent } from '@bazis/form/components/parts/control-field-end.component';
import { BazisControlSelectEndComponent } from '@bazis/form/components/parts/control-select-end.component';
import { BazisControlSearchEndComponent } from '@bazis/form/components/parts/control-search-end.component';
import { BazisSelectNativeComponent } from '@bazis/form/components/parts/select-native.component';
import { BazisAutocompleteSelectedValueComponent } from '@bazis/form/components/parts/autocomplete-selected-value.component';
import { BazisItemWithIconValueComponent } from '@bazis/form/components/parts/item-with-icon-value.component';
import { BazisListHintComponent } from '@bazis/form/components/parts/list-hint.component';
import { BazisMultipleSelectionItemComponent } from '@bazis/form/components/parts/multiple-selection-item.component';
import { BazisMultipleSelectionOptionComponent } from '@bazis/form/components/parts/multiple-selection-option.component';

import { BazisInputDefaultComponent } from '@bazis/form/components/base/inputs/input-default.component';
import { BazisInputOptionsComponent } from '@bazis/form/components/base/options/input-options.component';
import { BazisSwitcherComponent } from '@bazis/form/components/base/switcher.component';
import { BazisInputOptionsButtonComponent } from '@bazis/form/components/base/options/input-options-button.component';
import { BazisInputOptionsCardComponent } from '@bazis/form/components/base/options/input-options-card.component';
import { BazisSelectComponent } from '@bazis/form/components/base/selects/select.component';
import { BazisSelectTextComponent } from '@bazis/form/components/base/selects/select-text.component';

import { BazisInputFileComponent } from '@bazis/form/components/base/inputs/input-file.component';
import { BazisFormService } from '@bazis/form/services/form.service';
import { BazisFormErrorComponent } from '@bazis/form/components/parts/form-error.component';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { BazisInputNumberComponent } from '@bazis/form/components/base/inputs/input-number.component';
import { BazisDraftInnerDefaultComponent } from '@bazis/form/components/helpers/draft-inner-default.component';
import { BazisInputMapPointComponent } from '@bazis/form/components/complex/input-map-point.component';
import { BazisSelectOnMapComponent } from '@bazis/form/components/base/select-on-map.component';
import { BazisInputContactsComponent } from '@bazis/form/components/complex/input-contacts.component';
import { BazisInputScheduleComponent } from '@bazis/form/components/complex/input-schedule.component';
import { BazisInputVehicleNumberComponent } from '@bazis/form/components/base/inputs/input-vehicle-number.component';
import { BazisInputTextArrayComponent } from '@bazis/form/components/base/inputs/input-text-array.component';
import { BazisEstimatedValueComponent } from '@bazis/form/components/base/estimated-value.components';
import { BazisBrowseDefaultComponent } from '@bazis/form/components/browse/browse-default.component';
import { BazisBrowseOptionsComponent } from '@bazis/form/components/browse/browse-options.component';
import { BazisBrowseTextFieldComponent } from '@bazis/form/components/browse/browse-text-field.component';
import { BazisBrowseOptionsButtonComponent } from '@bazis/form/components/browse/browse-options-button.component';
import { BazisInputSymbolComponent } from '@bazis/form/components/base/inputs/input-symbol.component';

// Date/Time components
import { DatepickerModule } from '@bazis/shared/components/web/datepicker/datepicker.module';
import { TimepickerModule } from '@bazis/shared/components/web/timepicker/timepicker.module';
import { BazisTimeintervalComponent } from '@bazis/form/components/datetime/timeinterval.component';
import { BazisInputDateComponent } from '@bazis/form/components/datetime/input-date.component';
import { BazisInputDatetimeComponent } from '@bazis/form/components/datetime/input-datetime.component';
import { BazisInputTimeintervalComponent } from '@bazis/form/components/datetime/input-timeinterval.component';
import { BazisInputDateintervalComponent } from '@bazis/form/components/datetime/input-dateinterval.component';
import { BazisInputDateTimeintervalComponent } from '@bazis/form/components/datetime/input-date-timeinterval.component';
import { BazisSingleInputTimeintervalComponent } from '@bazis/form/components/datetime/input-single-timeinterval.component';
import { BazisInputDateSingleTimeintervalComponent } from '@bazis/form/components/datetime/input-date-single-timeinterval.component';
import { BazisButtonDateComponent } from '@bazis/form/components/datetime/button-date.component';
import { BazisMenuInputDateComponent } from '@bazis/form/components/datetime/menu-input-date.component';
import { BazisDropdownDateIntervalComponent } from '@bazis/form/components/datetime/dropdown-dateinterval.component';

import { BazisEntityTransitsComponent } from '@bazis/form/components/helpers/entity-transits.components';
import { BazisEditEntityDefaultComponent } from '@bazis/form/components/helpers/edit-entity-default.component';

import { BazisButtonSearchComponent } from '@bazis/form/components/base/autocomplete/button-search.component';
import { BazisRequiredMarkComponent } from '@bazis/form/components/parts/required-mark.component';
import { BazisTransitModalComponent } from '@bazis/form/components/helpers/transit-modal.component';
import { BazisInputNumberButtonsComponent } from '@bazis/form/components/base/inputs/input-number-buttons.component';
import { BazisDisplayErrorComponent } from '@bazis/form/components/parts/display-error.component';
import { BazisInputAddressComponent } from '@bazis/form/components/base/hint/input-address.component';
import { BazisSelectDateComponent } from '@bazis/form/components/datetime/select-during-date.component';
import { BazisDropdownSearchComponent } from '@bazis/form/components/base/autocomplete/dropdown-search.component';
import { BazisInputPasswordComponent } from '@bazis/form/components/base/inputs/input-password.component';
import { BazisSortComponent } from '@bazis/form/components/base/selects/sort.component';
import { BazisInputFileButtonComponent } from '@bazis/form/components/base/inputs/input-file-button.component';
import { BazisSharedModule } from '@bazis/shared/shared.module';
import { BazisMapModule } from '@bazis/map/map.module';
import { NgxMaskDirective } from 'ngx-mask';
import { BazisInputHintComponent } from '@bazis/form/components/base/hint/input-hint.component';
import { BazisInputTinComponent } from '@bazis/form/components/base/hint/input-tin.component';
import { BazisAutocompleteGroupedDataComponent } from '@bazis/form/components/base/autocomplete/autocomplete-grouped-data.component';
import { BazisInputOptionsGroupedComponent } from '@bazis/form/components/base/options/input-options-grouped.component';
import { BazisInputRangeComponent } from '@bazis/form/components/base/input-range.component';
import { BazisInputTinOrganizationComponent } from '@bazis/form/components/base/hint/input-tin-organization.component';
import { BazisAutocompleteGroupedComponent } from '@bazis/form/components/base/autocomplete/autocomplete-grouped.component';
import { BazisInputPhoneComponent } from '@bazis/form/components/base/inputs/input-phone.component';

import { BazisAutocompleteComponent } from '@bazis/form/components/base/autocomplete/autocomplete.component';
import { BazisAutocompleteStaticComponent } from '@bazis/form/components/base/autocomplete/autocomplete-static.component';
import { ForceLatinDirective } from '@bazis/form/directives/forceLatin.directive';

const DECLARATIONS = [
    BazisControlValueAccessor,
    BazisControlFieldStartComponent,
    BazisControlFieldEndComponent,
    BazisControlSelectEndComponent,
    BazisControlSearchEndComponent,
    BazisSelectNativeComponent,
    BazisAutocompleteSelectedValueComponent,
    BazisItemWithIconValueComponent,
    BazisListHintComponent,
    BazisMultipleSelectionItemComponent,
    BazisMultipleSelectionOptionComponent,

    BazisButtonSearchComponent,
    BazisDropdownSearchComponent,

    BazisInputDefaultComponent,
    BazisInputNumberComponent,
    BazisInputNumberButtonsComponent,
    BazisInputMapPointComponent,
    BazisInputVehicleNumberComponent,
    BazisInputHintComponent,
    BazisInputTinComponent,
    BazisInputPhoneComponent,

    BazisInputOptionsComponent,
    BazisInputFileComponent,
    BazisInputFileButtonComponent,
    BazisSelectComponent,
    BazisAutocompleteGroupedComponent,
    BazisSelectOnMapComponent,
    BazisSelectTextComponent,
    BazisSelectDateComponent,
    BazisSortComponent,
    BazisSwitcherComponent,
    BazisInputOptionsButtonComponent,
    BazisInputOptionsCardComponent,
    BazisAutocompleteGroupedDataComponent,
    BazisAutocompleteStaticComponent,
    BazisInputOptionsGroupedComponent,

    BazisFormErrorComponent,
    BazisDisplayErrorComponent,
    BazisRequiredMarkComponent,

    BazisBrowseDefaultComponent,
    BazisBrowseOptionsComponent,
    BazisBrowseOptionsButtonComponent,
    BazisBrowseTextFieldComponent,

    BazisTimeintervalComponent,
    BazisInputDateComponent,
    BazisInputDatetimeComponent,
    BazisInputTimeintervalComponent,
    BazisInputDateintervalComponent,
    BazisInputDateTimeintervalComponent,
    BazisButtonDateComponent,
    BazisMenuInputDateComponent,
    BazisDropdownDateIntervalComponent,
    BazisSingleInputTimeintervalComponent,
    BazisInputDateSingleTimeintervalComponent,

    BazisInputTextArrayComponent,
    BazisInputContactsComponent,
    BazisInputScheduleComponent,
    BazisInputTinOrganizationComponent,

    BazisDraftInnerDefaultComponent,
    BazisEditEntityDefaultComponent,
    BazisEntityTransitsComponent,
    BazisEstimatedValueComponent,
    BazisTransitModalComponent,
    BazisInputAddressComponent,
    BazisInputPasswordComponent,
    BazisInputSymbolComponent,
    BazisInputRangeComponent,

    BazisAutocompleteComponent,

    ForceLatinDirective,
];

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ReactiveFormsModule,
        DatepickerModule,
        TimepickerModule,
        BazisSharedModule,
        BazisMapModule,
        TranslocoModule,
        NgxMaskDirective,
    ],
    exports: [...DECLARATIONS],
    declarations: [...DECLARATIONS],
    providers: [BazisFormService, { provide: TRANSLOCO_SCOPE, useValue: ['form'] }],
})
export class BazisFormModule {}
